import { cloneRow } from "../factories";
import { findGroup, locateRow } from "../find";
import { isRow } from "../sections";
import type { Row } from "../types";
import { canDeleteRow, clampSectionInsertIndex } from "../row-policy";
import {
  isContiguousSiblingRowSelection,
  resolveOrderedSiblingRowIds,
  resolveOrderedTopLevelRowIds,
} from "../selection/row-bulk-ops";
import type { EditorDraft } from "./types";
import { applyRowMultiSelectionChange, applySelectionChange } from "./draft";

function deleteDocumentRows(draft: EditorDraft, rowIds: string[]): boolean {
  const indices: number[] = [];
  for (const rowId of rowIds) {
    const located = locateRow(draft.document.sections, rowId);
    if (!located || located.location.parent !== "document") {
      return false;
    }
    if (!canDeleteRow(draft.document, rowId)) {
      return false;
    }
    indices.push(located.location.sectionIndex);
  }

  const uniqueIndices = [...new Set(indices)].sort((left, right) => right - left);
  if (draft.document.sections.length - uniqueIndices.length < 1) {
    return false;
  }

  const lowestIndex = Math.min(...indices);
  for (const index of uniqueIndices) {
    draft.document.sections.splice(index, 1);
  }

  const next =
    draft.document.sections[
      Math.min(lowestIndex, draft.document.sections.length - 1)
    ];
  if (next) {
    applySelectionChange(
      draft,
      isRow(next) ? { kind: "row", rowId: next.id } : { kind: "group", groupId: next.id },
    );
  } else {
    draft.selection = { kind: "email" };
    draft.selectedRowIds = [];
    draft.primaryRowId = null;
    draft.anchorRowId = null;
  }
  return true;
}

function deleteGroupRows(draft: EditorDraft, groupId: string, rowIds: string[]): boolean {
  const group = findGroup(draft.document.sections, groupId);
  if (!group) {
    return false;
  }

  const indices = rowIds.map(rowId => group.rows.findIndex(row => row.id === rowId));
  if (indices.some(index => index < 0)) {
    return false;
  }
  if (!rowIds.every(rowId => canDeleteRow(draft.document, rowId))) {
    return false;
  }

  const uniqueIndices = [...new Set(indices)].sort((left, right) => right - left);
  const lowestIndex = Math.min(...indices);
  for (const index of uniqueIndices) {
    group.rows.splice(index, 1);
  }

  const nextRow =
    group.rows[lowestIndex] ?? group.rows[lowestIndex - 1];
  if (nextRow) {
    applyRowMultiSelectionChange(draft, {
      selectedRowIds: [nextRow.id],
      primaryRowId: nextRow.id,
      anchorRowId: nextRow.id,
    });
  } else {
    draft.selection = { kind: "group", groupId: group.id };
    draft.selectedRowIds = [];
    draft.primaryRowId = null;
    draft.anchorRowId = null;
  }
  return true;
}

export function deleteTopLevelRows(draft: EditorDraft, rowIds: string[]): boolean {
  const resolved = resolveOrderedSiblingRowIds(draft.document, rowIds);
  if (!resolved || resolved.rowIds.length < 2) {
    return false;
  }

  if (resolved.list.parent === "group") {
    return deleteGroupRows(draft, resolved.list.groupId, resolved.rowIds);
  }

  return deleteDocumentRows(draft, resolved.rowIds);
}

function duplicateDocumentRows(draft: EditorDraft, rowIds: string[]): boolean {
  let insertAfterIndex = -1;
  const copies: Row[] = [];
  for (const rowId of rowIds) {
    const located = locateRow(draft.document.sections, rowId);
    if (!located || located.location.parent !== "document") {
      return false;
    }
    insertAfterIndex = Math.max(insertAfterIndex, located.location.sectionIndex);
    copies.push(cloneRow(located.row));
  }

  if (insertAfterIndex < 0) {
    return false;
  }

  draft.document.sections.splice(insertAfterIndex + 1, 0, ...copies);
  const copiedIds = copies.map(copy => copy.id);
  applyRowMultiSelectionChange(draft, {
    selectedRowIds: copiedIds,
    primaryRowId: copiedIds[copiedIds.length - 1] ?? null,
    anchorRowId: copiedIds[0] ?? null,
  });
  return true;
}

function duplicateGroupRows(draft: EditorDraft, groupId: string, rowIds: string[]): boolean {
  const group = findGroup(draft.document.sections, groupId);
  if (!group) {
    return false;
  }

  const indices = rowIds.map(rowId => group.rows.findIndex(row => row.id === rowId));
  if (indices.some(index => index < 0)) {
    return false;
  }

  const insertAfterIndex = Math.max(...indices);
  const copies: Row[] = [];
  for (const rowId of rowIds) {
    const located = locateRow(draft.document.sections, rowId);
    if (!located) {
      return false;
    }
    copies.push(cloneRow(located.row));
  }

  group.rows.splice(insertAfterIndex + 1, 0, ...copies);
  const copiedIds = copies.map(copy => copy.id);
  applyRowMultiSelectionChange(draft, {
    selectedRowIds: copiedIds,
    primaryRowId: copiedIds[copiedIds.length - 1] ?? null,
    anchorRowId: copiedIds[0] ?? null,
  });
  return true;
}

export function duplicateTopLevelRows(draft: EditorDraft, rowIds: string[]): boolean {
  const resolved = resolveOrderedSiblingRowIds(draft.document, rowIds);
  if (!resolved || resolved.rowIds.length < 2) {
    return false;
  }

  if (resolved.list.parent === "group") {
    return duplicateGroupRows(draft, resolved.list.groupId, resolved.rowIds);
  }

  return duplicateDocumentRows(draft, resolved.rowIds);
}

export function moveContiguousTopLevelRowsToSlot(
  draft: EditorDraft,
  rowIds: string[],
  slotIndex: number,
): boolean {
  const ordered = resolveOrderedTopLevelRowIds(draft.document, rowIds);
  if (!ordered) {
    return false;
  }

  if (!isContiguousSiblingRowSelection(draft.document, ordered.rowIds)) {
    return false;
  }

  const indices = ordered.rowIds.map(rowId => {
    const located = locateRow(draft.document.sections, rowId);
    if (!located || located.location.parent !== "document") {
      return -1;
    }
    return located.location.sectionIndex;
  });
  if (indices.some(index => index < 0)) {
    return false;
  }

  const sectionsToMove = indices.map(
    index => draft.document.sections[index],
  );
  if (sectionsToMove.some(section => section === undefined)) {
    return false;
  }

  for (let index = indices.length - 1; index >= 0; index -= 1) {
    draft.document.sections.splice(indices[index], 1);
  }

  let insertAt = clampSectionInsertIndex(draft.document.sections, slotIndex);
  insertAt -= indices.filter(index => index < slotIndex).length;
  draft.document.sections.splice(insertAt, 0, ...sectionsToMove);

  applyRowMultiSelectionChange(draft, {
    selectedRowIds: ordered.rowIds,
    primaryRowId: ordered.rowIds[ordered.rowIds.length - 1] ?? null,
    anchorRowId: ordered.rowIds[0] ?? null,
  });
  return true;
}

export function moveContiguousGroupRowsToSlot(
  draft: EditorDraft,
  groupId: string,
  rowIds: string[],
  slotIndex: number,
): boolean {
  const resolved = resolveOrderedSiblingRowIds(draft.document, rowIds);
  if (
    !resolved ||
    resolved.list.parent !== "group" ||
    resolved.list.groupId !== groupId
  ) {
    return false;
  }

  if (!isContiguousSiblingRowSelection(draft.document, resolved.rowIds)) {
    return false;
  }

  const group = findGroup(draft.document.sections, groupId);
  if (!group) {
    return false;
  }

  const indices = resolved.rowIds.map(rowId =>
    group.rows.findIndex(row => row.id === rowId),
  );
  if (indices.some(index => index < 0)) {
    return false;
  }

  const rowsToMove: Row[] = [];
  for (const index of indices) {
    const row = group.rows[index];
    if (row === undefined) {
      return false;
    }
    rowsToMove.push(row);
  }

  for (let index = indices.length - 1; index >= 0; index -= 1) {
    group.rows.splice(indices[index], 1);
  }

  let insertAt = Math.max(0, Math.min(slotIndex, group.rows.length));
  insertAt -= indices.filter(index => index < slotIndex).length;
  group.rows.splice(insertAt, 0, ...rowsToMove);

  applyRowMultiSelectionChange(draft, {
    selectedRowIds: resolved.rowIds,
    primaryRowId: resolved.rowIds[resolved.rowIds.length - 1] ?? null,
    anchorRowId: resolved.rowIds[0] ?? null,
  });
  return true;
}

export function moveContiguousSiblingRowsByDelta(
  draft: EditorDraft,
  rowIds: string[],
  delta: -1 | 1,
): boolean {
  const resolved = resolveOrderedSiblingRowIds(draft.document, rowIds);
  if (!resolved || !isContiguousSiblingRowSelection(draft.document, resolved.rowIds)) {
    return false;
  }

  if (resolved.list.parent === "group") {
    const group = findGroup(draft.document.sections, resolved.list.groupId);
    if (!group) {
      return false;
    }

    const indices = resolved.rowIds.map(rowId =>
      group.rows.findIndex(row => row.id === rowId),
    );
    if (indices.some(index => index < 0)) {
      return false;
    }

    const edgeIndex = delta < 0 ? Math.min(...indices) : Math.max(...indices);
    const targetIndex = edgeIndex + delta;
    if (targetIndex < 0 || targetIndex >= group.rows.length) {
      return false;
    }

    const rowsToMove: Row[] = [];
    for (const index of indices) {
      const row = group.rows[index];
      if (row === undefined) {
        return false;
      }
      rowsToMove.push(row);
    }

    for (let index = indices.length - 1; index >= 0; index -= 1) {
      group.rows.splice(indices[index], 1);
    }

    let insertAt = targetIndex;
    if (delta > 0) {
      const removedBeforeTarget = indices.filter(
        index => index < edgeIndex + 1,
      ).length;
      insertAt = edgeIndex + 1 - removedBeforeTarget + 1;
    }

    group.rows.splice(insertAt, 0, ...rowsToMove);
  } else {
    const indices = resolved.rowIds.map(rowId => {
      const located = locateRow(draft.document.sections, rowId);
      if (!located || located.location.parent !== "document") {
        return -1;
      }
      return located.location.sectionIndex;
    });
    if (indices.some(index => index < 0)) {
      return false;
    }

    const edgeIndex = delta < 0 ? Math.min(...indices) : Math.max(...indices);
    const targetIndex = edgeIndex + delta;
    if (targetIndex < 0 || targetIndex >= draft.document.sections.length) {
      return false;
    }

    const sectionsToMove = indices.map(index => draft.document.sections[index]);
    if (sectionsToMove.some(section => section === undefined || !isRow(section))) {
      return false;
    }

    for (let index = indices.length - 1; index >= 0; index -= 1) {
      draft.document.sections.splice(indices[index], 1);
    }

    let insertAt = targetIndex;
    if (delta > 0) {
      const removedBeforeTarget = indices.filter(
        index => index < edgeIndex + 1,
      ).length;
      insertAt = edgeIndex + 1 - removedBeforeTarget + 1;
    }

    draft.document.sections.splice(insertAt, 0, ...sectionsToMove);
  }

  applyRowMultiSelectionChange(draft, {
    selectedRowIds: resolved.rowIds,
    primaryRowId: resolved.rowIds[resolved.rowIds.length - 1] ?? null,
    anchorRowId: resolved.rowIds[0] ?? null,
  });
  return true;
}
